import React, { createContext, useContext, useState, useEffect, useCallback, type ComponentType } from 'react';
import { useSession, signOut } from 'next-auth/react';
import axios from 'axios';
import type { UserDto } from '@/types/user';
import { createLogger } from '@/utils/logger';

const logger = createLogger('User Context');

/**
 * User Context for managing user state across the application
 * Reduces redundant API calls by providing cached user data
 */
interface UserContextType {
  user: UserDto | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<UserDto>) => void;
  clearUser: () => void;
  // Helper methods
  isSubscribed: boolean;
  hasCredits: boolean;
  isOnboardingComplete: boolean;
  // Debug information
  cacheAge: number;
  cacheValid: boolean;
}

const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  error: null,
  refreshUser: () => Promise.resolve(),
  updateUser: () => undefined,
  clearUser: () => undefined,
  isSubscribed: false,
  hasCredits: false,
  isOnboardingComplete: false,
  cacheAge: 0,
  cacheValid: false,
});

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: React.ReactNode;
  initialUser?: UserDto | null;
}

export const UserProvider = ({ children, initialUser = null }: UserProviderProps) => {
  const { data: session, status } = useSession();
  const [user, setUser] = useState<UserDto | null>(initialUser);
  const [loading, setLoading] = useState(!initialUser);
  const [error, setError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState(initialUser ? Date.now() : 0);

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  logger.debug(`Initialized with initial user: ${initialUser?.email || 'none'}`);

  /**
   * Fetch user data from API
   */
  const fetchUser = useCallback(
    async (force = false) => {
      const fetchStart = Date.now();

      if (!session?.user?.id) {
        logger.debug('No session found, skipping fetch');
        setLoading(false);
        return;
      }

      // Check if we have recent data and not forcing refresh
      const now = Date.now();
      const cacheAge = now - lastFetch;
      const isCacheValid = !force && user && cacheAge < CACHE_DURATION;

      if (isCacheValid) {
        logger.debug(`✅ Using cached data for ${user.email} (age: ${cacheAge}ms)`);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        logger.debug(
          `${force ? '🔄 Force fetching' : '🌐 Fetching'} user data for: ${session.user.email}`
        );

        const apiStart = Date.now();
        const response = await axios.post('/api/user/get_user', {
          user_id: session.user.id,
        });

        const apiTime = Date.now() - apiStart;
        const totalTime = Date.now() - fetchStart;

        logger.debug(`✅ API call completed in: ${apiTime}ms`);
        logger.debug(
          `User data fetched for: ${response.data.email} (subscribed: ${response.data.subscribed})`
        );

        setUser(response.data as UserDto);
        setLastFetch(now);

        logger.debug(`Total fetch time: ${totalTime}ms`);
      } catch (err: unknown) {
        const totalTime = Date.now() - fetchStart;
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        const statusCode = (err as { response?: { status?: number } })?.response?.status;
        logger.error(`❌ API call failed after ${totalTime}ms`, {
          error: errorMessage,
          status: statusCode,
        });

        // A 401 (session no longer accepted) or a definitive "User not found" 404
        // (account deleted — e.g. from another device while this one kept its
        // still-validly-signed JWT cookie) can never succeed on retry. Sign out via
        // NextAuth, which clears the correct cookie names for the current scheme,
        // instead of stranding the device in an error state it can only escape by
        // manually clearing cookies.
        const apiError = (err as { response?: { data?: { error?: unknown } } })?.response?.data
          ?.error;
        const userGone =
          statusCode === 404 && typeof apiError === 'string' && /user not found/i.test(apiError);
        if (statusCode === 401 || userGone) {
          logger.error('Session user no longer valid — signing out', { status: statusCode });
          await signOut({ callbackUrl: '/auth/login' });
          return;
        }
        setError(
          ((err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            'Failed to fetch user data') as string
        );

        // If we have stale user data, keep using it
        if (!user) {
          logger.debug('Using session fallback data');
          // Fall back to basic session data
          setUser({
            id: session.user.id ?? '',
            email: session.user.email ?? '',
            name: session.user.name,
            finished_onboarding: (session.user as { finished_onboarding?: boolean })
              .finished_onboarding ?? false,
            subscribed: (session.user as { subscribed?: boolean }).subscribed ?? false,
            plan: 'free',
            credits_available: 0,
            plan_credit_limit: 0,
          });
        } else {
          logger.debug('Keeping existing user data due to error');
        }
      } finally {
        setLoading(false);
      }
    },
    [session?.user?.id, session?.user?.email, session?.user?.name, user, lastFetch]
  );

  /**
   * Refresh user data (force fetch)
   */
  const refreshUser = useCallback(() => {
    logger.debug(`🔄 Refresh requested for: ${session?.user?.email || 'unknown'}`);
    return fetchUser(true);
  }, [fetchUser, session?.user?.email]);

  /**
   * Update user data optimistically
   */
  const updateUser = useCallback(
    (updates: Partial<UserDto>) => {
      logger.debug(
        `📝 Optimistic update for: ${user?.email || 'unknown'}`,
        { updates }
      );

      setUser((prevUser) => ({
        ...(prevUser ?? ({} as UserDto)),
        ...updates,
      }));
      // Reset cache timer to prevent immediate refetch
      setLastFetch(Date.now());
    },
    [user?.email]
  );

  /**
   * Clear user data
   */
  const clearUser = useCallback(() => {
    logger.debug('🗑️ Clearing user data');
    setUser(null);
    setLastFetch(0);
    setError(null);
  }, []);

  // Effect to fetch user data when session becomes available
  useEffect(() => {
    if (status === 'authenticated' && session?.user?.id && !user) {
      logger.debug('Session authenticated, fetching user data');
      fetchUser();
    } else if (status === 'unauthenticated') {
      logger.debug('Session unauthenticated, clearing user data');
      clearUser();
      setLoading(false);
    }
  }, [status, session?.user?.id, user, fetchUser, clearUser]);

  // Effect to use initial user data from SSR
  useEffect(() => {
    if (initialUser && !user) {
      logger.debug(`🚀 Using initial SSR data for: ${initialUser.email}`);
      setUser(initialUser);
      setLastFetch(Date.now());
      setLoading(false);
    }
  }, [initialUser, user]);

  // Effect to log cache status changes
  useEffect(() => {
    if (user) {
      const cacheAge = Date.now() - lastFetch;
      const timeToExpiry = CACHE_DURATION - cacheAge;

      if (cacheAge > 0) {
        logger.debug(
          `📊 Cache status for ${user.email}: age=${cacheAge}ms, expires in=${timeToExpiry}ms`
        );
      }
    }
  }, [user, lastFetch, CACHE_DURATION]);

  const value: UserContextType = {
    user,
    loading,
    error,
    refreshUser,
    updateUser,
    clearUser,
    // Helper methods
    isSubscribed: user?.subscribed ?? false,
    hasCredits: (user?.credits_available ?? 0) > 0,
    isOnboardingComplete: user?.finished_onboarding ?? false,
    // Debug information
    cacheAge: user ? Date.now() - lastFetch : 0,
    cacheValid: user ? Date.now() - lastFetch < CACHE_DURATION : false,
  };

  return (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
};

/**
 * HOC to provide user context to pages
 */
export const withUserContext = <P extends Record<string, unknown>>(
  Component: ComponentType<P>
) => {
  return function WrappedComponent(props: P & { user?: UserDto | null }) {
    logger.debug(
      `HOC wrapping component with user: ${props.user?.email || 'none'}`
    );
    return (
      <UserProvider initialUser={props.user ?? null}>
        <Component {...(props as P)} />
      </UserProvider>
    );
  };
};

/**
 * Hook for components that need to trigger user updates
 * after performing actions that modify user data
 */
export const useUserActions = () => {
  const { user, updateUser, refreshUser } = useUser();

  const updateCredits = useCallback(
    (creditChange: number) => {
      logger.debug(`💰 Updating credits by: ${creditChange}`);
      updateUser({
        credits_available: Math.max(
          0,
          (user?.credits_available ?? 0) + creditChange
        ),
      });
    },
    [updateUser, user?.credits_available]
  );

  const markOnboardingComplete = useCallback(() => {
    logger.debug('✅ Marking onboarding complete');
    updateUser({ finished_onboarding: true });
  }, [updateUser]);

  const updateSubscription = useCallback(
    (subscriptionData: { subscribed: boolean; plan: string; stripe_subscription?: unknown }) => {
      logger.debug('🔄 Updating subscription', { subscriptionData });
      updateUser({
        subscribed: subscriptionData.subscribed,
        plan: subscriptionData.plan as UserDto['plan'],
        stripe_subscription: subscriptionData.stripe_subscription as UserDto['stripe_subscription'],
      });
    },
    [updateUser]
  );

  const updateProfile = useCallback(
    (profileData: { name?: string; company_name?: string; about_company?: string }) => {
      logger.debug('👤 Updating profile', { profileData });
      updateUser({
        name: profileData.name ?? undefined,
        company_name: profileData.company_name,
        about_company: profileData.about_company,
      });
    },
    [updateUser]
  );

  return {
    updateCredits,
    markOnboardingComplete,
    updateSubscription,
    updateProfile,
    refreshUser,
  };
};

export default UserContext;
